import { useState, useEffect, useCallback, useRef } from 'react';
import { db, realtime } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { getBandwidthSummary } from '../services/stockService';

export const useDashboardStats = () => {
  const { user, profile } = useAuth();
  const [stats, setStats] = useState({
    balance: 0,
    totalTopups: 0,
    totalSpent: 0,
    activeProxies: 0,
    dataUsed: {
      residentialBasic: 0,
      residentialPremium: 0,
      datacenter: 0,
      isp: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const fetchStats = useCallback(async () => {
    if (!user || !profile) return;

    try {
      if (!hasFetched.current) {
        setLoading(true);
      }

      const { data, error } = await db.profiles.getDashboardStats(user.id);
      if (error) throw error;

      const dbStats = {
        balance: parseFloat(data?.balance) || 0,
        totalTopups: parseFloat(data?.total_topups) || 0,
        totalSpent: parseFloat(data?.total_spent) || 0,
        activeProxies: data?.active_proxies || 0,
        dataUsed: {
          residentialBasic: parseFloat(data?.residential_basic_used) || 0,
          residentialPremium: parseFloat(data?.residential_premium_used) || 0,
          datacenter: parseFloat(data?.datacenter_used) || 0,
          isp: parseFloat(data?.isp_used) || 0,
        },
      };

      const hasBasic = !!profile?.stock_subuser_id_basic;
      const hasPremium = !!profile?.stock_subuser_id_premium;

      if (hasBasic || hasPremium) {
        const [basicResult, premiumResult] = await Promise.allSettled([
          hasBasic ? getBandwidthSummary('basic') : Promise.resolve({ success: false }),
          hasPremium ? getBandwidthSummary('premium') : Promise.resolve({ success: false }),
        ]);

        if (basicResult.status === 'fulfilled' && basicResult.value.success && basicResult.value.data) {
          const raw = basicResult.value.data;
          const d = raw.data || raw;
          const used = parseFloat(d.total_used_gb);
          if (!isNaN(used)) dbStats.dataUsed.residentialBasic = used;
        }

        if (premiumResult.status === 'fulfilled' && premiumResult.value.success && premiumResult.value.data) {
          const raw = premiumResult.value.data;
          const d = raw.data || raw;
          const used = parseFloat(d.total_used_gb);
          if (!isNaN(used)) dbStats.dataUsed.residentialPremium = used;
        }
      }

      setStats(dbStats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  }, [user, profile]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (profile) {
      setStats(prev => ({
        ...prev,
        balance: parseFloat(profile.balance) || 0,
        totalTopups: parseFloat(profile.total_topups) || 0,
        totalSpent: parseFloat(profile.total_spent) || 0,
      }));
    }
  }, [profile]);

  useEffect(() => {
    if (!user) return;

    const channel = realtime.subscribeToProfile(user.id, (payload) => {
      if (payload.new) {
        setStats(prev => ({
          ...prev,
          balance: parseFloat(payload.new.balance) || 0,
          totalTopups: parseFloat(payload.new.total_topups) || 0,
          totalSpent: parseFloat(payload.new.total_spent) || 0,
        }));
      }
    });

    return () => {
      realtime.unsubscribe(channel);
    };
  }, [user]);

  return { stats, loading, error, refresh: fetchStats };
};

export default useDashboardStats;
